export function parseJsonInput(value) {
  if (!value || !value.trim()) {
    return null;
  }

  return JSON.parse(value);
}

export function formatJsonValue(value) {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'string') {
    try {
      return JSON.stringify(JSON.parse(value), null, 2);
    } catch (error) {
      return value;
    }
  }

  return JSON.stringify(value, null, 2);
}

export function formatResolvedValue(value) {
  if (value === null || value === undefined) {
    return 'null';
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2);
  }

  return String(value);
}

export function formatValuePreview(value, valueType) {
  if (valueType === 'json') {
    return formatJsonValue(value);
  }

  if (valueType === 'bool') {
    return value ? 'true' : 'false';
  }

  if (valueType === 'null' || value === null) {
    return 'null';
  }

  return String(value ?? '');
}

export function getValueTypeGuidance(valueType) {
  switch (valueType) {
    case 'bool':
      return 'Use the switch to send a real boolean value.';
    case 'int':
      return 'Only whole numbers are valid here.';
    case 'float':
      return 'Use a decimal number and preserve the precision you need.';
    case 'json':
      return 'Provide a valid JSON array or object. The backend stores it as JSON text.';
    case 'null':
      return 'This item is intentionally written back as null.';
    case 'string':
    default:
      return 'Update the runtime string value exactly as the app should read it.';
  }
}

export function buildItemEditorState(item) {
  const type = item?.value_type || 'string';
  const storedValue = item?.value;
  const isNullType = type === 'null';

  return {
    valueType: type,
    boolValue: storedValue === 'true' || storedValue === true,
    intValue: storedValue ?? '',
    floatValue: storedValue ?? '',
    stringValue: storedValue ?? '',
    jsonValue: formatJsonValue(storedValue),
    nullValue: isNullType,
    reason: '',
  };
}

export function buildConfigItemPayload(editorState) {
  switch (editorState.valueType) {
    case 'bool':
      return editorState.boolValue;
    case 'int':
      return Number.parseInt(editorState.intValue, 10);
    case 'float':
      return Number(editorState.floatValue);
    case 'json':
      return parseJsonInput(editorState.jsonValue);
    case 'null':
      return null;
    case 'string':
    default:
      return editorState.stringValue;
  }
}

export function validateConfigItemEditor(editorState) {
  const errors = {};

  if (editorState.valueType === 'int') {
    if (editorState.intValue === '' || editorState.intValue === null) {
      errors.value = 'Integer value is required.';
    } else if (!/^-?\d+$/.test(String(editorState.intValue).trim())) {
      errors.value = 'Enter a valid integer.';
    }
  }

  if (editorState.valueType === 'float') {
    if (editorState.floatValue === '' || editorState.floatValue === null) {
      errors.value = 'Decimal value is required.';
    } else if (Number.isNaN(Number(editorState.floatValue))) {
      errors.value = 'Enter a valid decimal number.';
    }
  }

  if (editorState.valueType === 'json') {
    try {
      const parsed = parseJsonInput(editorState.jsonValue);
      if (parsed === null || typeof parsed !== 'object') {
        errors.value = 'JSON value must be an array or object.';
      }
    } catch (error) {
      errors.value = 'Enter valid JSON before saving.';
    }
  }

  if (editorState.valueType === 'string' && editorState.stringValue === undefined) {
    errors.value = 'String value is required.';
  }

  return errors;
}
